/**
 * Browser-local pins for shared Vega chats the user has actually opened.
 * Recent chats shows personal history + these pins (not every shared chat).
 */

import type { AgentConversationOrigin } from '@gonfalon/observability-graphql/schemas';

export type OpenedSharedChatPin = {
  conversation_id: string;
  initiated_at: string;
  last_user_message_at: string;
  message_preview: string;
  title?: string | null;
  tags?: ReadonlyArray<{ key: string; value: string }> | null;
  origin?: AgentConversationOrigin | null;
  agent_kind?: string | null;
};

const STORAGE_PREFIX = 'vega.openedSharedChats.';
/** Cap pins per project so localStorage stays bounded. */
export const OPENED_SHARED_CHATS_MAX = 50;

export const OPENED_SHARED_CHATS_CHANGED_EVENT = 'vega-opened-shared-chats-changed';

export type OpenedSharedChatsChangedDetail = {
  projectId: string;
};

function storageKey(projectId: string): string {
  return `${STORAGE_PREFIX}${projectId}`;
}

/** Storage key for a project's pins — used by cross-tab `storage` listeners. */
export function openedSharedChatsStorageKey(projectId: string): string {
  return storageKey(projectId);
}

function isUnknownRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isOpenedSharedChatPin(value: unknown): value is OpenedSharedChatPin {
  if (!isUnknownRecord(value)) {
    return false;
  }
  return (
    typeof value.conversation_id === 'string' &&
    typeof value.initiated_at === 'string' &&
    typeof value.last_user_message_at === 'string' &&
    typeof value.message_preview === 'string'
  );
}

/** Narrow a window event to this module's custom event and return its projectId. */
export function getOpenedSharedChatsChangedProjectId(event: Event): string | undefined {
  if (!(event instanceof CustomEvent)) {
    return undefined;
  }
  const detail: unknown = event.detail;
  if (!isUnknownRecord(detail) || typeof detail.projectId !== 'string') {
    return undefined;
  }
  return detail.projectId;
}

function sortByRecency(pins: OpenedSharedChatPin[]): OpenedSharedChatPin[] {
  return [...pins].sort((a, b) => {
    const aMs = Date.parse(a.last_user_message_at) || Date.parse(a.initiated_at) || 0;
    const bMs = Date.parse(b.last_user_message_at) || Date.parse(b.initiated_at) || 0;
    return bMs - aMs;
  });
}

function writePins(projectId: string, pins: OpenedSharedChatPin[]): void {
  if (!projectId || typeof localStorage === 'undefined') {
    return;
  }
  try {
    const trimmed = sortByRecency(pins).slice(0, OPENED_SHARED_CHATS_MAX);
    localStorage.setItem(storageKey(projectId), JSON.stringify(trimmed));
    const detail: OpenedSharedChatsChangedDetail = { projectId };
    window.dispatchEvent(new CustomEvent(OPENED_SHARED_CHATS_CHANGED_EVENT, { detail }));
  } catch {
    // localStorage unavailable or quota exceeded — ignore
  }
}

export function readOpenedSharedChats(projectId: string): OpenedSharedChatPin[] {
  if (!projectId || typeof localStorage === 'undefined') {
    return [];
  }
  try {
    const raw = localStorage.getItem(storageKey(projectId));
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isOpenedSharedChatPin);
  } catch {
    return [];
  }
}

/**
 * Upsert a pin. By default bumps `last_user_message_at` to now so opening a
 * shared chat (even an old one) resurfaces it near the top of Recent — matching
 * "I just opened this" rather than the original prompt time.
 */
export function upsertOpenedSharedChat(
  projectId: string,
  pin: Omit<OpenedSharedChatPin, 'last_user_message_at'> & { last_user_message_at?: string },
  options?: { bumpToNow?: boolean },
): void {
  if (!projectId || !pin.conversation_id) {
    return;
  }
  const bumpToNow = options?.bumpToNow !== false;
  const nowIso = new Date().toISOString();
  const nextPin: OpenedSharedChatPin = {
    conversation_id: pin.conversation_id,
    initiated_at: pin.initiated_at || nowIso,
    last_user_message_at: bumpToNow ? nowIso : (pin.last_user_message_at ?? nowIso),
    message_preview: pin.message_preview ?? '',
    title: pin.title,
    tags: pin.tags,
    origin: pin.origin,
    agent_kind: pin.agent_kind,
  };
  const existing = readOpenedSharedChats(projectId).filter((p) => p.conversation_id !== nextPin.conversation_id);
  writePins(projectId, [nextPin, ...existing]);
}

/** Bump an existing pin's activity time (e.g. after the user sends a prompt). */
export function bumpOpenedSharedChat(projectId: string, conversationId: string, atMs: number = Date.now()): void {
  if (!projectId || !conversationId) {
    return;
  }
  const pins = readOpenedSharedChats(projectId);
  const index = pins.findIndex((p) => p.conversation_id === conversationId);
  if (index < 0) {
    return;
  }
  const updated = [...pins];
  updated[index] = { ...updated[index], last_user_message_at: new Date(atMs).toISOString() };
  writePins(projectId, updated);
}
